import { type IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faChevronDown, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { type ReactNode } from 'react';

import { buttonVariants, type ButtonVariants } from '../Button/Button';
import { dropdownMenuVariants } from '../DropdownMenu/DropdownMenu';
import { cn } from '../lib/utils';

interface SplitButtonItem {
  label: ReactNode;
  description?: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

interface SplitButtonProps {
  children: ReactNode;
  onClick: () => void;
  items: SplitButtonItem[];
  color?: ButtonVariants['color'];
  variant?: ButtonVariants['variant'];
  size?: ButtonVariants['size'];
  isLoading?: boolean;
  disabled?: boolean;
  leftIcon?: IconDefinition;
  /** Accessible name for the menu half of the button. */
  menuLabel?: string;
  className?: string;
}

function SplitButton({
  children,
  onClick,
  items,
  color = 'primary',
  variant = 'default',
  size = 'md',
  isLoading = false,
  disabled = false,
  leftIcon,
  menuLabel = 'More options',
  className
}: SplitButtonProps) {
  const { content, item: itemCls } = dropdownMenuVariants();
  const isDisabled = disabled || isLoading;

  return (
    <Menu as="div" className={cn('relative inline-flex', className)}>
      <button
        type="button"
        disabled={isDisabled}
        onClick={onClick}
        className={cn(buttonVariants({ variant, color, size }), 'rounded-r-none')}>
        {isLoading ? (
          <FontAwesomeIcon icon={faSpinner} spin className="shrink-0" />
        ) : (
          leftIcon && <FontAwesomeIcon icon={leftIcon} className="shrink-0" />
        )}
        {children}
      </button>
      <MenuButton
        aria-label={menuLabel}
        disabled={isDisabled}
        className={cn(
          buttonVariants({ variant, color, size }),
          'rounded-l-none border-l border-l-white/40 px-2'
        )}>
        <FontAwesomeIcon icon={faChevronDown} className="h-3 w-3 shrink-0" />
      </MenuButton>
      <MenuItems anchor="bottom end" className={cn(content(), '[--anchor-gap:4px] min-w-56')}>
        {items.map((menuItem, index) => (
          <MenuItem key={index} disabled={menuItem.disabled}>
            <button
              type="button"
              onClick={menuItem.onClick}
              disabled={menuItem.disabled}
              className={cn(itemCls(), 'flex-col items-start gap-0.5')}>
              <span className="font-medium text-uniform-gray-900 dark:text-uniform-gray-100">
                {menuItem.label}
              </span>
              {menuItem.description && (
                <span className="text-xs text-uniform-gray-500 dark:text-uniform-gray-400">
                  {menuItem.description}
                </span>
              )}
            </button>
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  );
}

export { SplitButton };
export type { SplitButtonItem, SplitButtonProps };
